import { get } from 'idb-keyval'
import { OfflineStorage } from 'src/services/offline-storage'
import { readImageFromFileSystem } from 'src/services/offline-images'
import { withAnswersLock } from '@/services/offline-answers-lock'
import { legacyRowKey } from '@/utils/legacyAnswers'
import { MetaRepository } from '../repositories/MetaRepository'
import * as domainOccurrences from '../domain/occurrences'
import { isNativePlatform } from './platform'

/**
 * migrate-v1-answers.js — migrador ÚNICA-VEZ-POR-DISPOSITIVO de las answers/addons pendientes del
 * almacén legacy v1 (answers.json, vía OfflineStorage) hacia offline-v2.
 *
 * CONTEXTO: cuando un dispositivo cambia del modo offline legacy (OfflineStorage: answers.json en
 * localStorage web / filesystem nativo) al flag
 * offline-v2 (SQLite), las respuestas que quedaron pendientes en answers.json del v1 se volvían
 * HUÉRFANAS: el worker v2 no las conoce y el v1 ya no drena -> se perdían al cerrar/sincronizar.
 * Este migrador las re-encola por la fachada de dominio v2 (saveAnswer / addAddon, idempotentes por
 * occ+item / client_uuid) y luego LIMPIA el almacén legacy.
 *
 * REGLAS (cero-pérdida — endurecidas tras PR review):
 *  - SOLO LECTURA del v1 (OfflineStorage.getAnswers + idb-keyval/FS para los binarios de foto) + su
 *    propia API de limpieza (quita solo las filas migradas). NO se modifican archivos de src/offline/.
 *  - Idempotente por dispositivo: marca meta `migr:v1answers:done`. Segunda corrida = no-op.
 *  - FALLO DE LECTURA != VACÍO REAL [review Hugo r3 #2]: si OfflineStorage.getAnswers() LANZA, se
 *    aborta con `readFailed` SIN limpiar ni marcar done. Antes el catch dejaba `legacyAnswers = []`,
 *    lo que hacía `total = 0` y `migrated === total` -> 0 === 0 -> limpiaba v1 y marcaba done PARA
 *    SIEMPRE. Un fallo transitorio (IndexedDB bloqueado en cold start) destruía capturas pendientes.
 *  - EL ALMACÉN v1 SOLO SE LIMPIA Y SE MARCA DONE SI TODO EL DATO MIGRÓ (migrated === total). Si una
 *    entrada falló (excepción al re-encolar), v1 NO se vacía ni se marca done -> el próximo boot
 *    reintenta (la idempotencia v2 por UNIQUE(occ,item)/client_uuid evita doble-encolar las ya OK).
 *    [PR review #1] Antes la limpieza+marca corrían INCONDICIONALMENTE -> una entry mala perdía el
 *    dato para siempre.
 *  - ENTRIES IRRECUPERABLES / GIVE-UP ACOTADO [review Hugo r3 #8]: una entry que SIEMPRE lanza (dato
 *    corrupto, occurrence_id inválido) mantenía `migrated < total` para siempre -> cada boot re-leía y
 *    re-procesaba todo el lote, y v1 nunca se limpiaba. Ahora, tras MAX_ATTEMPTS boots parciales, se
 *    deja de reintentar. DIFERENCIA CLAVE con "limpiar v1": las entries que fallaron NO se borran, se
 *    CONSERVAN en v1 (aunque la relectura no las traiga) y solo se quitan las que sí migraron. Marcar done
 *    con `migrated + unrecoverable === total` y vaciar v1 sería reintroducir el bug de arriba por otra
 *    puerta: el catch por-entry también dispara con fallos de ESCRITURA de SQLite (no solo con dato
 *    corrupto), así que un fallo transitorio en TODAS las entries daría migrated=0/unrecoverable=total
 *    -> vaciaría v1. Con el write-back, nada que haya fallado se destruye nunca.
 *  - FOTOS [PR review #10]: una entry v1 con foto pendiente (answer_ref/image_ref/localPath o value
 *    base64) trae su binario en idb-keyval (web) o en filesystem (nativo). Se RE-HIDRATA y se pasa
 *    como photoBase64 a la fachada v2 para que PhotoStore cree el photo_meta y el drain la suba. Si el
 *    binario ya NO es recuperable (evictado), se migra el DATO igual (no migrarlo perdería más) y se
 *    registra la pérdida de la foto (photosLost) — reintentar no recuperaría un binario ausente.
 *  - BEST-EFFORT total: todo en try/catch; NUNCA bloquea ni rompe el boot.
 *  - Un addon (tiene `type`) -> domain.addAddon; una answer normal -> domain.saveAnswer.
 */

const MIGRATION_KEY = 'migr:v1answers:done'
// Contador de boots con migración PARCIAL. Acota el re-procesado del lote cuando una entry es
// permanentemente mala; ver GIVE-UP ACOTADO en la cabecera.
const ATTEMPTS_KEY = 'migr:v1answers:attempts'
const MAX_ATTEMPTS = 3

/** ¿La entry v1 lleva una foto pendiente (ref local o base64 inline)? */
function entryHasPhoto (entry) {
  return !!(
    entry.answer_ref ||
    entry.image_ref ||
    entry.localPath ||
    (typeof entry.value === 'string' && entry.value.startsWith('data:image')) ||
    (typeof entry.answer === 'string' && entry.answer.startsWith('data:image'))
  )
}

/**
 * Re-hidrata el binario base64 de una foto pendiente del v1.
 *  - base64 inline en value/answer -> se usa directo.
 *  - nativo con localPath -> se lee del filesystem.
 *  - web -> idb-keyval por (answer_ref || image_ref), el MISMO store que usa offline-images.js.
 * @returns {Promise<string|null>} dataURL base64, o null si no es recuperable.
 */
async function rehydrateV1Photo (entry) {
  if (typeof entry.value === 'string' && entry.value.startsWith('data:image')) return entry.value
  if (typeof entry.answer === 'string' && entry.answer.startsWith('data:image')) return entry.answer
  const ref = entry.answer_ref || entry.image_ref
  if (!ref && !entry.localPath) return null
  try {
    if (isNativePlatform() && entry.localPath) {
      const b64 = await readImageFromFileSystem(entry.localPath)
      return (typeof b64 === 'string' && b64.startsWith('data:image')) ? b64 : null
    }
    if (ref) {
      const b64 = await get(ref)
      return (typeof b64 === 'string' && b64.startsWith('data:image')) ? b64 : null
    }
  } catch (e) {
    console.error('[migrate-v1-answers] no se pudo re-hidratar la foto v1:', e)
  }
  return null
}

// Quita del almacén v1 las filas migradas sobre una lectura fresca, dentro del candado. Las de `keep` quedan siempre.
async function removeMigratedFromV1 (migratedRows, keep = []) {
  const keyOf = row => {
    try {
      return legacyRowKey(row)
    } catch {
      return null
    }
  }
  const migratedKeys = new Set(migratedRows.map(row => legacyRowKey(row)))
  await withAnswersLock(async () => {
    const remaining = ((await OfflineStorage.getAnswers()) || []).filter(row => row && !migratedKeys.has(keyOf(row)))
    const remainingKeys = new Set(remaining.map(row => keyOf(row)))
    const missing = keep.filter(row => keyOf(row) === null ? !remaining.includes(row) : !remainingKeys.has(keyOf(row)))
    await OfflineStorage.setAnswers([...remaining, ...missing])
  })
}

/**
 * Migra las answers/addons pendientes del v1 a offline-v2. Una sola vez por dispositivo.
 * @returns {Promise<{ migrated: number, total?: number, photosLost?: number, unrecoverable?: number,
 *   partial?: boolean, skipped?: boolean, readFailed?: boolean, gaveUp?: boolean }>}
 */
export async function migrateV1Answers () {
  try {
    if (await MetaRepository.has(MIGRATION_KEY)) {
      return { migrated: 0, skipped: true }
    }

    // FALLO DE LECTURA != VACÍO REAL: si esto lanza NO se puede distinguir "no hay nada que migrar" de
    // "no se pudo leer", así que se aborta sin tocar v1 -> el próximo boot reintenta (v2 es idempotente).
    let legacyAnswers
    try {
      legacyAnswers = (await OfflineStorage.getAnswers()) || []
    } catch (e) {
      console.error('[migrate-v1-answers] no se pudo leer v1; se reintenta en el próximo boot:', e)
      return { migrated: 0, readFailed: true }
    }

    const total = legacyAnswers.filter(Boolean).length
    let migrated = 0
    const migratedRows = []
    let photosLost = 0
    // Entries que lanzaron al re-encolar. En el give-up se conservan en v1: nunca se borra algo que no se pudo migrar.
    const failed = []

    for (const entry of legacyAnswers) {
      if (!entry) continue
      try {
        // Re-hidratar la foto (si la hay) ANTES de re-encolar, para que v2 cree el photo_meta.
        let photoBase64
        if (entryHasPhoto(entry)) {
          photoBase64 = await rehydrateV1Photo(entry)
          if (!photoBase64) {
            // Binario irrecuperable en v1: se migra el DATO igual; reintentar no lo recuperaría.
            photosLost++
            console.warn('[migrate-v1-answers] foto v1 irrecuperable (se migra el dato sin foto), item:', entry.item_id)
          }
        }

        if (entry.type) {
          // Addon (comentario/documento/foto): client_uuid DETERMINISTA para que el coalesce de v2
          // haga su trabajo ENTRE REINTENTOS (el guard cero-pérdida reintenta TODAS las entries cuando
          // la migración fue parcial, incl. las ya migradas; sin uuid estable, addAddon hacía newId()
          // en cada retry -> fila+op+captura DUPLICADOS que llegan al server — las answers están a salvo
          // por UNIQUE(occ,item), los addons NO). Discriminador de contenido (answer_ref/image_ref/answer)
          // por si v1 tiene N addons del mismo tipo por item -> no se colapsan entre sí. [review Hugo]
          const disc = entry.answer_ref ?? entry.image_ref ?? entry.answer ?? ''
          await domainOccurrences.addAddon({
            occurrence_id: String(entry.occurrence_id),
            item_id: entry.item_id,
            type: entry.type,
            answer: entry.answer,
            answer_ref: entry.answer_ref,
            client_uuid: `mig:${entry.occurrence_id}:${entry.item_id}:${entry.type}:${disc}`,
            photoBase64
          })
        } else {
          // Answer normal: idempotente por UNIQUE(occurrence_id, item_id) en v2.
          await domainOccurrences.saveAnswer({
            occurrence_id: String(entry.occurrence_id),
            item_id: entry.item_id,
            value: entry.value,
            current_points: entry.current_points,
            total_points: entry.total_points,
            extra_properties: entry.extra_properties,
            // TKT-2424: conservar la hora real de captura del v1 (si no, saveAnswer sembraría now() y la
            // ejecución migrada volvería a mostrar 0/1 min, ahora además enviada al server).
            created_at: entry.created_at,
            updated_at: entry.updated_at,
            photoBase64
          })
        }
        migrated++
        migratedRows.push(entry)
      } catch (e) {
        // Una entrada mala no debe abortar el lote ni el boot; pero SÍ impide limpiar v1 (abajo).
        failed.push(entry)
        console.error('[migrate-v1-answers] entrada no migrada:', e)
      }
    }

    // CERO-PÉRDIDA [PR review #1]: solo limpiar v1 + marcar done si TODO el dato migró.
    if (migrated === total) {
      try {
        await removeMigratedFromV1(migratedRows)
      } catch (e) {
        // Si la limpieza falla, NO marcar done: el próximo boot reintenta (v2 idempotente).
        console.error('[migrate-v1-answers] no se pudo limpiar el almacén v1 (no se marca done):', e)
        return { migrated, photosLost }
      }
      await MetaRepository.put(MIGRATION_KEY)
      return { migrated, photosLost }
    }

    // MIGRACIÓN PARCIAL. Reintentar es lo correcto las primeras veces (el fallo puede ser transitorio),
    // pero no para siempre: se cuenta el boot y tras MAX_ATTEMPTS se deja de reintentar.
    const prevAttempts = Number(await MetaRepository.get(ATTEMPTS_KEY))
    const attempts = (Number.isFinite(prevAttempts) ? prevAttempts : 0) + 1
    await MetaRepository.put(ATTEMPTS_KEY, String(attempts))

    if (attempts >= MAX_ATTEMPTS) {
      // GIVE-UP ACOTADO: las que migraron salen de v1; las que fallaron se QUEDAN en v1 (no se borran).
      // Así el lote deja de re-procesarse en cada boot sin destruir nada irrecuperable.
      try {
        await removeMigratedFromV1(migratedRows, failed)
      } catch (e) {
        console.error('[migrate-v1-answers] no se pudo reescribir el almacén v1 (no se marca done):', e)
        return { migrated, total, photosLost, unrecoverable: failed.length, partial: true }
      }
      await MetaRepository.put(MIGRATION_KEY)
      console.warn(
        `[migrate-v1-answers] give-up tras ${attempts} boots: ${migrated}/${total} migradas; ` +
        `${failed.length} irrecuperable(s) SE CONSERVAN en el almacén v1 (no se borran) y se marca done`
      )
      return { migrated, total, photosLost, unrecoverable: failed.length, gaveUp: true }
    }

    console.warn(
      `[migrate-v1-answers] migración PARCIAL (intento ${attempts}/${MAX_ATTEMPTS}): ${migrated}/${total} entries — ` +
      'NO se limpia v1 ni se marca done; reintenta en el próximo boot'
    )
    return { migrated, total, photosLost, unrecoverable: failed.length, partial: true }
  } catch (e) {
    // Best-effort total: nunca bloquea el boot.
    console.error('[migrate-v1-answers] migración abortada (best-effort):', e)
    return { migrated: 0 }
  }
}
